import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, Observable, switchMap, take } from 'rxjs';

import { REMOTE_PICKUP_PATH, ROUTES } from '../remote-pickup/routes';
import { ContextService } from '../shared/services/context/context.service';
import { VIEW_TYPES } from '../shared/utils/constants';
import { selectSettingIsActivated, selectSettingLoadSettled } from '../stores/settings';

@Injectable()
export class AppGuardService implements CanActivate {
  activationUrl = `/${VIEW_TYPES.SETUP}`;
  restrictedRoutes = [
    `/${VIEW_TYPES.TTW}`,
    `/${VIEW_TYPES.FRONT_LOADING}`,
    `/${VIEW_TYPES.REAR_LOADING}`,
    `/${VIEW_TYPES.RETAIL}`,
    this.activationUrl,
  ];

  constructor(
    private store: Store,
    private router: Router,
    private contextService: ContextService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const currentUrl = state.url.split('?')[0];
    const baseContext = this.extractBaseContext(currentUrl);
    const existingContext = this.contextService.getEntryContext();

    const isActivated$ = this.store.select(selectSettingLoadSettled).pipe(
      filter((settled) => settled),
      take(1),
      switchMap(() => this.store.select(selectSettingIsActivated)),
      map((isActivated: boolean) => {
        if (!isActivated) {
          if (currentUrl === this.activationUrl) return true;
          this.router.navigate([this.activationUrl]);
          return false;
        }

        const isRemotePickupContext = existingContext === REMOTE_PICKUP_PATH;
        const isRestricted = this.isRestrictedRoute(currentUrl);

        if (isRemotePickupContext && isRestricted) {
          this.router.navigate([ROUTES.REMOTE_PICKUP]);
          return false;
        }

        if (!existingContext || (existingContext && !isRemotePickupContext)) {
          this.contextService.setEntryContext(baseContext);
        }

        return true;
      })
    );

    return isActivated$;
  }

  private isRestrictedRoute(url: string): boolean {
    return this.restrictedRoutes.some((prefix) => url.startsWith(prefix));
  }

  private extractBaseContext(url: string): string {
    const parts = url.split('/').filter(Boolean);
    return parts.length > 0 ? parts[0] : '';
  }
}
